// components/screener/StockScreener.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../AuthContext';
import {
    Container, Box, Typography, CircularProgress, Paper, useMediaQuery
} from '@mui/material';
import useExchanges from '../../hooks/useExchanges';
import useIndustries from '../../hooks/useIndustries';
import useSectors from '../../hooks/useSectors';
import useCountries from '../../hooks/useCountries';
import TitleAndInfo from '../TitleAndInfo';
import { exportToCsv } from '../../utils/exportToCsv';

import useScreener from './useScreener';
import { VIEWS, COLUMNS } from './screenerConfig';
import FilterBar from './FilterBar';
import FilterPanel from './FilterPanel';
import AddFilterDialog from './AddFilterDialog';
import ResultsTable from './ResultsTable';

const StockScreener = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const exchanges = useExchanges();
    const industries = useIndustries();
    const sectors = useSectors();
    const countries = useCountries();

    // Filter options for select/multiSelect filters
    const filterOptions = useMemo(() => ({
        exchanges,
        sectors,
        industries,
        countries,
    }), [exchanges, sectors, industries, countries]);

    // Main screener hook
    const screener = useScreener();

    // UI state — collapsed by default on mobile
    const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
    const [isFiltersExpanded, setIsFiltersExpanded] = useState(!isMobile);

    // Active filter IDs for the dialog checkmarks
    const activeFilterIds = useMemo(
        () => new Set(screener.activeFilters.map(f => f.id)),
        [screener.activeFilters]
    );

    // Toggle filter from the dialog
    const handleToggleFilter = useCallback((filterDef) => {
        if (activeFilterIds.has(filterDef.id)) {
            screener.removeFilter(filterDef.id);
        } else {
            screener.addFilter(filterDef);
        }
    }, [activeFilterIds, screener]);

    // Search handler — collapse filters on mobile after search
    const handleSearch = useCallback(() => {
        screener.search();
        if (isMobile) setIsFiltersExpanded(false);
    }, [screener, isMobile]);

    // Clear handler
    const handleClear = useCallback(() => {
        screener.clear();
    }, [screener]);

    // Export handler
    const handleExport = useCallback(() => {
        const viewConfig = VIEWS.find(v => v.id === screener.currentView) || VIEWS[0];
        const columns = viewConfig.columns.map(colId => COLUMNS[colId]).filter(Boolean).map(col => ({
            field: col.field,
            headerName: col.headerName,
        }));
        exportToCsv('stock_screener.csv', columns, screener.results);
    }, [screener.results, screener.currentView]);

    // Content configuration
    const titleTop = "Stock Screener";
    const title = "Complete Market Analysis Tool";
    const info = "Screen stocks using comprehensive criteria including price, moving averages, market cap, financials, analyst ratings, and company profile data. Filter stocks based on multiple metrics to find the perfect matches for your investment strategy.(defaults to US exchanges. add Exchange filter to change.)";

    const hasResults = screener.results.length > 0;
    const showExport = user?.plan?.name === 'Premium' && hasResults;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
            <TitleAndInfo titleTop={titleTop} title={title} info={info} />

            {/* Filter section */}
            <Paper
                variant="stats"
                sx={{
                    p: 2,
                    borderRadius: 2,
                    mt: 3,
                }}
            >
                <FilterBar
                    activeFilterCount={screener.activeFilters.length}
                    selectedScreenName={screener.selectedScreenName}
                    onAddFilter={() => setIsAddFilterOpen(true)}
                    onSearch={handleSearch}
                    onClear={handleClear}
                    onExport={handleExport}
                    showExport={showExport}
                    onPredefinedScreen={screener.applyPredefinedScreen}
                    isFiltersExpanded={isFiltersExpanded}
                    onToggleFilters={() => setIsFiltersExpanded(!isFiltersExpanded)}
                    isLoading={screener.isLoading}
                    totalResults={screener.totalResults}
                />

                <FilterPanel
                    filters={screener.activeFilters}
                    onRemove={screener.removeFilter}
                    onUpdate={screener.updateFilterValues}
                    filterOptions={filterOptions}
                    isExpanded={isFiltersExpanded}
                />
            </Paper>

            {/* Add Filter Dialog */}
            <AddFilterDialog
                open={isAddFilterOpen}
                onClose={() => setIsAddFilterOpen(false)}
                activeFilterIds={activeFilterIds}
                onToggleFilter={handleToggleFilter}
            />

            {/* Error State */}
            {screener.error && (
                <Box sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.08)' : 'rgba(244,67,54,0.05)',
                    border: `1px solid ${theme.palette.error.main}30`,
                }}>
                    <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                        {screener.error}
                    </Typography>
                </Box>
            )}

            {/* Loading State */}
            {screener.isLoading && !hasResults && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress size={36} />
                </Box>
            )}

            {/* Empty State */}
            {!screener.isLoading && !hasResults && screener.queryId && (
                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                        No results found
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Try adjusting your filters or using a different preset screen.
                    </Typography>
                </Box>
            )}

            {/* Initial State — no search yet */}
            {!screener.isLoading && !hasResults && !screener.queryId && !screener.error && (
                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                        Add filters and click <strong>Search</strong> to find stocks, or select a preset screen.
                    </Typography>
                </Box>
            )}

            {/* Results */}
            {hasResults && (
                <Paper
                    variant="stats"
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 2,
                    }}
                >
                    <ResultsTable
                        results={screener.results}
                        currentView={screener.currentView}
                        onViewChange={screener.switchView}
                        page={screener.page}
                        pageSize={screener.pageSize}
                        totalResults={screener.totalResults}
                        totalPages={screener.totalPages}
                        onPageChange={screener.goToPage}
                        onPageSizeChange={screener.changePageSize}
                        isLoading={screener.isLoading}
                        activeFilterIds={screener.activeFilters.map(f => f.id)}
                    />
                </Paper>
            )}
        </Container>
    );
};

export default StockScreener;